import { Trash2, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { useDialogFocusTrap } from '../../hooks/useDialogFocusTrap';
import { useI18n } from '../../i18n/useI18n';
import type { Wallet, WalletDeleteResolution, WalletType, WalletWrite } from '../../lib/api-types';
import { useCreateWallet, useDeleteWallet, useUpdateWallet } from './wallets.api';

interface WalletEditorDialogProps {
  readonly linkedMovements: number;
  readonly onClose: () => void;
  readonly onSaved?: () => void;
  readonly wallet?: Wallet;
  readonly wallets: readonly Wallet[];
}

const walletTypes: readonly WalletType[] = [
  'cash',
  'checking',
  'saving',
  'credit',
  'loan',
  'other',
];

const walletTypeLabels = {
  cash: 'wallets.typeCash',
  checking: 'wallets.typeChecking',
  credit: 'wallets.typeCredit',
  loan: 'wallets.typeLoan',
  other: 'wallets.typeOther',
  saving: 'wallets.typeSaving',
} as const;

export function WalletEditorDialog({
  linkedMovements,
  onClose,
  onSaved,
  wallet,
  wallets,
}: WalletEditorDialogProps) {
  const { t } = useI18n();
  const [draft, setDraft] = useState<Required<WalletWrite>>({
    balance: Math.abs(wallet?.balance ?? 0),
    color: wallet?.color ?? '#6F3FF2',
    name: wallet?.name ?? '',
    type: wallet?.type ?? 'cash',
  });
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const replacements = wallets.filter((candidate) => candidate.id !== wallet?.id);
  const [deleteStrategy, setDeleteStrategy] = useState<'delete-transactions' | 'move'>('move');
  const [replacementWalletId, setReplacementWalletId] = useState(replacements[0]?.id ?? '');
  const createWallet = useCreateWallet();
  const updateWallet = useUpdateWallet();
  const deleteWallet = useDeleteWallet();
  const dialogRef = useDialogFocusTrap(onClose);
  const mutationError = createWallet.error ?? updateWallet.error ?? deleteWallet.error;
  const deleteBlocked = wallet?.fixed === true;
  const isDebt = draft.type === 'credit' || draft.type === 'loan';

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const input = { ...draft, balance: isDebt ? -Math.abs(draft.balance) : 0 };
    if (wallet) await updateWallet.mutateAsync({ id: wallet.id, input });
    else await createWallet.mutateAsync(input);
    onSaved?.();
    onClose();
  };

  const remove = async () => {
    if (!wallet || deleteBlocked) return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    const resolution: WalletDeleteResolution | undefined =
      linkedMovements === 0
        ? undefined
        : deleteStrategy === 'delete-transactions'
          ? { strategy: 'delete-transactions' }
          : replacementWalletId
            ? { replacementWalletId, strategy: 'move' }
            : undefined;
    if (linkedMovements > 0 && resolution === undefined) return;
    await deleteWallet.mutateAsync({
      id: wallet.id,
      ...(resolution === undefined ? {} : { resolution }),
    });
    onClose();
  };

  return (
    <div className="dialog-backdrop" role="presentation">
      <section
        aria-labelledby="wallet-editor-title"
        aria-modal="true"
        className="movement-dialog wallet-dialog"
        ref={dialogRef}
        role="dialog"
      >
        <div className="dialog-heading">
          <div>
            <p className="section-kicker">{t('navigation.wallets')}</p>
            <h2 id="wallet-editor-title">{t(wallet ? 'wallets.edit' : 'wallets.add')}</h2>
          </div>
          <button
            aria-label={t('common.close')}
            className="icon-button"
            type="button"
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </div>

        <form className="movement-form" onSubmit={submit}>
          <label>
            {t('wallets.name')}
            <input
              autoFocus
              disabled={wallet?.fixed}
              maxLength={80}
              required
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          </label>
          {isDebt ? (
            <label>
              {t('wallets.debtAmount')}
              <input
                aria-label={t('wallets.debtAmount')}
                inputMode="decimal"
                max="1000000000"
                min="0"
                required
                step="0.01"
                type="number"
                value={draft.balance}
                onChange={(event) => setDraft({ ...draft, balance: Number(event.target.value) })}
              />
              <small>{t('wallets.debtAmountHelp')}</small>
            </label>
          ) : (
            <p className="wallet-ledger-note">{t('wallets.balanceFromMovements')}</p>
          )}
          <label>
            {t('wallets.type')}
            <select
              disabled={wallet?.fixed}
              value={draft.type}
              onChange={(event) => {
                const type = event.target.value as WalletType;
                setDraft({
                  ...draft,
                  balance: type === 'credit' || type === 'loan' ? draft.balance : 0,
                  type,
                });
              }}
            >
              {walletTypes.map((type) => (
                <option key={type} value={type}>
                  {t(walletTypeLabels[type])}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('wallets.color')}
            <span className="wallet-color-field">
              <input
                aria-label={t('wallets.color')}
                type="color"
                value={draft.color}
                onChange={(event) =>
                  setDraft({ ...draft, color: event.target.value.toUpperCase() })
                }
              />
              <input
                aria-label={`${t('wallets.color')} HEX`}
                pattern="#[0-9A-Fa-f]{6}"
                required
                value={draft.color}
                onChange={(event) => setDraft({ ...draft, color: event.target.value })}
              />
            </span>
          </label>

          {mutationError !== null && (
            <p className="form-error field-wide" role="alert">
              {t('wallets.error')}
            </p>
          )}
          {wallet && deleteBlocked && (
            <p className="wallet-delete-note field-wide">{t('wallets.deleteBlocked')}</p>
          )}
          {wallet && confirmingDelete && linkedMovements > 0 && !deleteBlocked && (
            <fieldset className="wallet-delete-resolution field-wide">
              <legend>{t('wallets.deleteLinked')}</legend>
              <label>
                <input
                  checked={deleteStrategy === 'move'}
                  name="wallet-delete-strategy"
                  type="radio"
                  onChange={() => setDeleteStrategy('move')}
                />
                <span>
                  <strong>{t('wallets.moveTransactions')}</strong>
                  <small>{t('wallets.moveTransactionsHelp')}</small>
                </span>
              </label>
              {deleteStrategy === 'move' &&
                (replacements.length ? (
                  <select
                    aria-label={t('wallets.moveTransactions')}
                    value={replacementWalletId}
                    onChange={(event) => setReplacementWalletId(event.target.value)}
                  >
                    {replacements.map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <small>{t('wallets.noReplacement')}</small>
                ))}
              <label>
                <input
                  checked={deleteStrategy === 'delete-transactions'}
                  name="wallet-delete-strategy"
                  type="radio"
                  onChange={() => setDeleteStrategy('delete-transactions')}
                />
                <span>
                  <strong>{t('wallets.deleteTransactions')}</strong>
                  <small>{t('wallets.deleteTransactionsHelp')}</small>
                </span>
              </label>
            </fieldset>
          )}
          <div className="movement-editor-actions field-wide">
            {wallet ? (
              <button
                className="button button-danger"
                disabled={
                  deleteBlocked ||
                  deleteWallet.isPending ||
                  (confirmingDelete &&
                    linkedMovements > 0 &&
                    deleteStrategy === 'move' &&
                    !replacementWalletId)
                }
                type="button"
                onClick={remove}
              >
                <Trash2 aria-hidden="true" />
                {confirmingDelete ? t('wallets.confirmDelete') : t('wallets.delete')}
              </button>
            ) : (
              <span />
            )}
            <div className="dialog-actions">
              <button className="button button-ghost" type="button" onClick={onClose}>
                {t('common.cancel')}
              </button>
              <button
                className="button button-primary"
                disabled={!draft.name.trim() || createWallet.isPending || updateWallet.isPending}
                type="submit"
              >
                {createWallet.isPending || updateWallet.isPending
                  ? t('common.saving')
                  : t('wallets.save')}
              </button>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
}
